import React, { useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, Snackbar, Typography } from '@mui/material';
import { PDFDocument, StandardFonts } from 'pdf-lib';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Document, Page, pdfjs } from 'react-pdf';
import { v4 as uuidv4 } from 'uuid';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import './PDFSignatureViewer.css';
import PublicSigneesSidebar from './PublicSignSidebar';
import SignatureDialog from './SignatureDialog';

pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';

const signatureDimensions = { width: 150, height: 60 };
export const ItemTypes = {
  SIGN_BUTTON: 'SIGN_BUTTON',
};

interface SignaturePosition {
  id?: string;
  page: number;
  x: number;
  y: number;
  signed?: boolean;
  signatureUrl?: string;
  signeeName?: string;
  signeeEmail?: string;
  signeeType?: string;
  source?: 'new' | 'loaded';
  signeeId?: string;
  fieldType?: 'signature' | 'initials'; // Add fieldType to distinguish between signature and initials
}

const DropZone: React.FC<{
  pageNumber: number;
  onDropSignature: ({
    item,
    page,
    x,
    y,
  }: {
    item: any;
    page: number;
    x: number;
    y: number;
  }) => void;
}> = ({ pageNumber, onDropSignature }) => {
  const dropRef = useRef<HTMLDivElement | null>(null);
  const { useDrop } = require('react-dnd');

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.SIGN_BUTTON,
    drop: (item: any, monitor: any) => {
      const offset = monitor.getClientOffset();
      const sourceOffset = monitor.getSourceClientOffset();
      const dropArea = dropRef.current;
      if (!offset || !dropArea || !sourceOffset) return;
      const rect = dropArea.getBoundingClientRect();
      const top = sourceOffset.y - rect.top;
      const left = sourceOffset.x - rect.left;
      onDropSignature({ item, page: pageNumber, x: left, y: top });
    },
    collect: (monitor: any) => ({
      isOver: !!monitor.isOver(),
    }),
  }));
  drop(dropRef);
  return (
    <div
      ref={dropRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        border: isOver ? '2px dashed green' : '2px dashed transparent',
        pointerEvents: 'auto',
      }}
    />
  );
};

const DraggableSignButton: React.FC<{
  sx?: React.CSSProperties;
  signatureItem?: SignaturePosition | null;
  enableSign?: boolean;
  onClickMethod?: () => void;
  signeeName?: string;
  signeeType?: string;
  disabled?: boolean;
  isHighlighted?: boolean;
}> = ({
  sx,
  signatureItem = null,
  enableSign = false,
  onClickMethod,
  signeeName,
  signeeType,
  disabled,
  isHighlighted = false,
  ...rest
}) => {
  const { useDrag } = require('react-dnd');
  if (enableSign && disabled && signatureItem?.source === 'loaded') {
    return null;
  }
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.SIGN_BUTTON,
    item: () => ({ ...signatureItem }),
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  // Highlight animation styles
  const highlightStyles = isHighlighted
    ? {
        animation: 'pulse 1s ease-in-out infinite',
        boxShadow: '0 0 20px rgba(255, 152, 0, 0.8)',
        border: '3px solid #ff9800',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
      }
    : {};

  return (
    <Button
      ref={!enableSign ? (drag as any) : null}
      onClick={() => {
        if (enableSign && onClickMethod && !disabled) {
          onClickMethod();
        }
      }}
      disabled={disabled}
      data-field-id={signatureItem?.id}
      style={{
        border: 'none',
        cursor: disabled ? 'not-allowed' : !enableSign ? 'move' : 'pointer',
        opacity: isDragging || disabled ? 0.5 : 1,
        backgroundColor: 'transparent',
        width: signatureDimensions.width,
        height: signatureDimensions.height,
        zIndex: 10,
        display: isDragging ? 'none' : 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '4px',
        paddingBottom: '8px',
        transition: 'all 0.3s ease',
        ...highlightStyles,
        ...(sx as React.CSSProperties),
      }}
      {...rest}
    >
      <div
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          marginBottom: '2px',
          color: isHighlighted ? '#ff9800' : 'inherit',
        }}
      >
        {signatureItem?.fieldType === 'initials'
          ? 'Initials Here'
          : 'Sign Here'}
      </div>
      {(!enableSign || (enableSign && signatureItem?.source !== 'loaded')) &&
        signeeName && (
          <div
            style={{
              fontSize: '10px',
              color: isHighlighted ? '#ff9800' : '#666',
              textAlign: 'center',
              borderTop: '1px solid #ccc',
              paddingTop: '2px',
              width: '100%',
            }}
          >
            {signeeName}
            {signeeType && (
              <div style={{ fontSize: '9px', color: '#888', marginTop: '1px' }}>
                ({signeeType})
              </div>
            )}
          </div>
        )}
    </Button>
  );
};

interface PublicPDFSignatureViewerProps {
  pdfUrl?: string;
  signerId?: string;
  fileLocation?: string;
  contractId?: string;
  emailId?: string;
  fullFilePath?: string;
}

const PublicPDFSignatureViewer: React.FC<PublicPDFSignatureViewerProps> = ({
  pdfUrl,
  signerId,
  fileLocation,
  contractId,
  emailId,
  fullFilePath,
}) => {
  // PDF URL from query or upload
  const [pdfFile, setPdfFile] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [selectedPostionIndex, setSelectedPostionIndex] = useState<
    number | null
  >(null);
  const [pdfUrlRender, setPdfUrlRender] = useState<string | null>(null);
  const [openSignature, setOpenSignature] = useState(false);
  const [userSignature, setUserSignature] = useState<{
    type: 'draw' | 'type';
    value: string;
    typedText?: string;
  } | null>(null);
  const [pageViewports, setPageViewports] = useState<
    Record<number, { width: number; height: number }>
  >({});
  const [signaturePositions, setSignaturePositions] = useState<
    SignaturePosition[]
  >([]);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  const [activeSignerId, setActiveSignerId] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false);
  const [pdfLoadError, setPdfLoadError] = useState<string | null>(null);
  const originalPdfBytesRef = useRef<ArrayBuffer | null>(null);
  const [signeeUuidMap, setSigneeUuidMap] = useState<Record<string, string>>(
    {}
  );

  // Auto-navigation state
  const [nextFieldToHighlight, setNextFieldToHighlight] =
    useState<SignaturePosition | null>(null);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Signees and contract details (public default)
  const [signees] = useState([
    {
      name: 'Public Signer',
      email: 'public@example.com',
      color: '#6ec6ca',
      fields: [
        { type: 'signature', label: 'Signature' },
        { type: 'text', label: 'Name' },
        { type: 'date', label: 'Date' },
      ],
    },
  ]);
  const [contractDetails] = useState({
    levels: 1,
    yearToRetain: 7,
    creatorRole: 'Public',
  });

  // Extract PDF URL from props or query on mount
  useEffect(() => {
    if (pdfUrl) {
      // Use the pdfUrl prop if provided
      setPdfFile(pdfUrl);
    } else {
      // Fallback to URL extraction for backward compatibility
      const urlParams = new URLSearchParams(window.location.search);
      let extracted = urlParams.get('pdfUrl') || urlParams.get('pathUrl');
      if (!extracted && window.location.href.includes('pathUrl=')) {
        extracted = decodeURIComponent(
          window.location.href.split('pathUrl=')[1]
        );
      }
      if (extracted) {
        setPdfFile(extracted);
      }
    }
    // Set active signer from props, URL, or default
    if (signerId) {
      // Use the signerId prop if provided
      setActiveSignerId(signerId);
    }
    // Fallback to URL extraction for backward compatibility
  }, [pdfUrl, signerId]);

  // File upload handler

  // Signee UUID logic
  const getSigneeUuid = (signeeEmail: string): string => {
    if (!signeeEmail) {
      return uuidv4(); // Fallback for invalid input
    }

    if (!signeeUuidMap[signeeEmail]) {
      const newUuid = uuidv4();
      setSigneeUuidMap((prev) => ({ ...prev, [signeeEmail]: newUuid }));
      return newUuid;
    }

    return signeeUuidMap[signeeEmail];
  };

  // Payload for sidebar
  const generatePayloadWithSigneeUuids = () => {
    const signeeGroups: Record<
      string,
      {
        signeeId: string;
        signeeName: string;
        signeeEmail: string;
        signeeType: string;
        fields: SignaturePosition[];
      }
    > = {};
    signaturePositions.forEach((position) => {
      if (position.signeeEmail) {
        const signeeId = getSigneeUuid(position.signeeEmail);
        if (!signeeGroups[position.signeeEmail]) {
          signeeGroups[position.signeeEmail] = {
            signeeId,
            signeeName: position.signeeName || 'Unknown',
            signeeEmail: position.signeeEmail,
            signeeType: position.signeeType || 'Signer',
            fields: [],
          };
        }
        signeeGroups[position.signeeEmail].fields.push(position);
      }
    });
    return {
      signees: Object.values(signeeGroups),
      totalFields: signaturePositions.length,
      contractDetails,
    };
  };

  // PDF load effect
  useEffect(() => {
    const loadPdf = async () => {
      if (!pdfFile) return;

      setIsPdfLoading(true);
      setPdfLoadError(null);

      try {
        // Add proper headers for S3 presigned URLs
        const fetchOptions: RequestInit = {
          method: 'GET',
          headers: {
            Accept: 'application/pdf',
          },
        };

        const response = await fetch(pdfFile, fetchOptions);

        if (!response.ok) {
          throw new Error(
            `Unable to load the document. Please check if the file exists and try again. (Error: ${response.status})`
          );
        }

        const bytes = await response.arrayBuffer();
        originalPdfBytesRef.current = bytes;

        try {
          const pdfDoc = await PDFDocument.load(bytes);
          const pages = pdfDoc.getPages();
          const existingPositions: SignaturePosition[] = [];
          try {
            const subject = pdfDoc.getSubject();
            if (
              subject?.includes(
                'Document with embedded signature fields - Metadata:'
              )
            ) {
              const metadataMatch = subject.match(/Metadata: (.+)$/);
              if (metadataMatch) {
                try {
                  const encodedMetadata = metadataMatch[1];
                  const metadataString = atob(encodedMetadata);
                  const metadata = JSON.parse(metadataString);
                  if (
                    metadata.signatureFields &&
                    Array.isArray(metadata.signatureFields)
                  ) {
                    existingPositions.push(
                      ...metadata.signatureFields.map((field: any) => ({
                        id: field.id,
                        page: field.page,
                        x: field.x,
                        y: field.y,
                        signed: field.signed || false,
                        signatureUrl: field.signatureUrl || '',
                        signeeName: field.signeeName || 'Signer',
                        signeeEmail: field.signeeEmail || '',
                        signeeType: field.signeeType || 'Signer',
                        signeeId: field.signeeId,
                        fieldType: field.fieldType || 'signature', // Restore field type from metadata
                        source: 'loaded',
                      }))
                    );
                    if (metadata.signeeUuidMap) {
                      setSigneeUuidMap(metadata.signeeUuidMap);
                    }
                  }
                } catch (parseError) {
                  // Error parsing signature field metadata
                }
              }
            }
          } catch (metadataError) {
            // No metadata
          }
          setSignaturePositions(existingPositions);
        } catch (error) {
          setSignaturePositions([]);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrlRender(url);

        // Auto-navigate to first unsigned field after a short delay
        setTimeout(() => {
          navigateToNextField();
        }, 2000);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        setPdfLoadError(errorMessage);
        setSnackbar({
          open: true,
          message: `Error loading PDF: ${errorMessage}`,
          severity: 'error',
        });
      } finally {
        setIsPdfLoading(false);
      }
    };
    loadPdf();
  }, [pdfFile]);

  // PDF page count
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // PDF load error handler
  const onDocumentLoadError = (error: Error) => {
    setPdfLoadError(error.message);
    setSnackbar({
      open: true,
      message: `Error loading PDF document: ${error.message}`,
      severity: 'error',
    });
  };

  // PDF page render error handler
  const onRenderError = (error: Error) => {
    // Don't show snackbar for page render errors as they might be temporary
  };

  // Retry PDF loading
  const retryPdfLoad = () => {
    setPdfLoadError(null);
    setIsPdfLoading(true);
    // Trigger the useEffect by updating pdfFile
    setPdfFile(pdfFile);
  };

  // DnD signature drop
  const handleDropSignature = async ({
    item,
    page,
    x,
    y,
  }: {
    item: any;
    page: number;
    x: number;
    y: number;
  }) => {
    const signeeId = getSigneeUuid(item?.signeeEmail);
    const newSign: SignaturePosition = {
      id: uuidv4(),
      page,
      x,
      y,
      signed: false,
      signeeName: item?.signeeName,
      signeeEmail: item?.signeeEmail,
      signeeType: item?.signeeType,
      signeeId,
      source: 'new',
      fieldType: item?.fieldType || 'signature', // Add fieldType from dropped item
    };
    setSignaturePositions((prev) => [...prev, newSign]);
  };

  // Utility to filter out duplicate signature fields
  const getUniqueSignaturePositions = (positions: SignaturePosition[]) => {
    const uniquePositions: SignaturePosition[] = [];
    const seen = new Set();
    for (const pos of positions) {
      const key = `${pos.page}_${Math.round(pos.x)}_${Math.round(pos.y)}_${pos.signeeId}`;
      if (!seen.has(key)) {
        uniquePositions.push(pos);
        seen.add(key);
      }
    }
    return uniquePositions;
  };

  // Find next unsigned field for the current signee
  const findNextUnsignedField = (
    currentFieldId?: string
  ): SignaturePosition | null => {
    // If no activeSignerId, try to find any unsigned field
    const targetSignerId = activeSignerId || signerId;

    if (!targetSignerId) {
      // If no signer ID at all, return the first unsigned field
      const uniquePositions = getUniqueSignaturePositions(signaturePositions);
      const unsignedFields = uniquePositions.filter(
        (pos) =>
          !pos.signed &&
          (pos.fieldType === 'signature' || pos.fieldType === 'initials')
      );
      return unsignedFields.length > 0 ? unsignedFields[0] : null;
    }

    const uniquePositions = getUniqueSignaturePositions(signaturePositions);
    const unsignedFields = uniquePositions.filter(
      (pos) =>
        pos.signeeId === targetSignerId &&
        !pos.signed &&
        (pos.fieldType === 'signature' || pos.fieldType === 'initials')
    );

    if (unsignedFields.length === 0) return null;

    // If no current field specified, return the first unsigned field
    if (!currentFieldId) {
      return unsignedFields[0];
    }

    // Find the current field index
    const currentIndex = unsignedFields.findIndex(
      (field) => field.id === currentFieldId
    );

    // Return the next field, or the first field if we're at the end
    if (currentIndex === -1 || currentIndex === unsignedFields.length - 1) {
      return unsignedFields[0]; // Loop back to first
    }

    return unsignedFields[currentIndex + 1];
  };

  // Navigate to and highlight the next field
  const navigateToNextField = (currentFieldId?: string) => {
    const nextField = findNextUnsignedField(currentFieldId);

    if (!nextField) {
      // No more fields to sign
      setSnackbar({
        open: true,
        message: 'All signature fields have been completed!',
        severity: 'success',
      });
      return;
    }

    setNextFieldToHighlight(nextField);
    setIsHighlighting(true);

    // Use a small delay to ensure the highlight is applied before scrolling
    setTimeout(() => {
      // Scroll to the page containing the next field
      const pageElement = pageRefs.current[nextField.page];

      if (pageElement) {
        // Find the scrollable container - look for the main PDF viewer container
        const scrollContainer =
          document.querySelector('[style*="overflow-y: auto"]') ||
          pageElement.closest('[style*="overflow-y: auto"]') ||
          document.documentElement;

        // Simple scroll to the page element first
        pageElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // Then fine-tune the position to center the field
        setTimeout(() => {
          const fieldElement = document.querySelector(
            `[data-field-id="${nextField.id}"]`
          );

          if (fieldElement) {
            fieldElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 300);
      }
    }, 100);

    // Remove highlight after 3 seconds
    setTimeout(() => {
      setIsHighlighting(false);
      setNextFieldToHighlight(null);
    }, 3000);
  };

  // Check if all signature fields are completed for the current signee
  const areAllFieldsCompleted = (): boolean => {
    const targetSignerId = activeSignerId || signerId;

    if (!targetSignerId) {
      // If no signer ID, check if there are any unsigned fields at all
      const uniquePositions = getUniqueSignaturePositions(signaturePositions);
      const unsignedFields = uniquePositions.filter(
        (pos) =>
          !pos.signed &&
          (pos.fieldType === 'signature' || pos.fieldType === 'initials')
      );
      return unsignedFields.length === 0;
    }

    const uniquePositions = getUniqueSignaturePositions(signaturePositions);
    const signeeFields = uniquePositions.filter(
      (pos) =>
        pos.signeeId === targetSignerId &&
        (pos.fieldType === 'signature' || pos.fieldType === 'initials')
    );

    if (signeeFields.length === 0) return true; // No fields to sign

    return signeeFields.every((field) => field.signed);
  };

  // Get the count of completed vs total fields for the current signee
  const getFieldCompletionStatus = () => {
    const targetSignerId = activeSignerId || signerId;

    if (!targetSignerId) {
      // If no signer ID, count all fields
      const uniquePositions = getUniqueSignaturePositions(signaturePositions);
      const allFields = uniquePositions.filter(
        (pos) => pos.fieldType === 'signature' || pos.fieldType === 'initials'
      );
      const completed = allFields.filter((field) => field.signed).length;
      const total = allFields.length;
      return { completed, total };
    }

    const uniquePositions = getUniqueSignaturePositions(signaturePositions);
    const signeeFields = uniquePositions.filter(
      (pos) =>
        pos.signeeId === targetSignerId &&
        (pos.fieldType === 'signature' || pos.fieldType === 'initials')
    );

    const completed = signeeFields.filter((field) => field.signed).length;
    const total = signeeFields.length;

    return { completed, total };
  };

  // PDF generation
  const generatePdfWithFields = async (
    positions: SignaturePosition[]
  ): Promise<Uint8Array> => {
    if (!originalPdfBytesRef.current)
      throw new Error('Original PDF bytes are not available.');
    const pdfDoc = await PDFDocument.load(originalPdfBytesRef.current);
    const pages = pdfDoc.getPages();
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const uniquePositions = getUniqueSignaturePositions(positions);
    for (const position of uniquePositions) {
      const pageIndex = position.page - 1;
      if (pageIndex < 0 || pageIndex >= pages.length) continue;
      const page = pages[pageIndex];
      const { width: pageWidthPoints, height: pageHeightPoints } =
        page.getSize();
      const renderedViewport = pageViewports[position.page];
      if (!renderedViewport) continue;
      const scale = pageWidthPoints / renderedViewport.width;
      const fieldWidthPx = signatureDimensions.width;
      const fieldHeightPx = signatureDimensions.height;
      const xInPoints = position.x * scale;
      const yInPoints = position.y * scale;
      const fieldWidthInPoints = fieldWidthPx * scale;
      const fieldHeightInPoints = fieldHeightPx * scale;
      const verticalAdjustment = fieldHeightInPoints * 0.25;
      const yPdf =
        pageHeightPoints - yInPoints - fieldHeightInPoints + verticalAdjustment;
      if (position.signed && position.signatureUrl) {
        try {
          const pngImage = await pdfDoc.embedPng(position.signatureUrl);
          page.drawImage(pngImage, {
            x: xInPoints,
            y: yPdf,
            width: fieldWidthInPoints,
            height: fieldHeightInPoints,
          });
        } catch (error) {
          // ignore
        }
      }
      // Do not draw name/type for any unsigned field
    }
    return pdfDoc.save();
  };

  // Add signature to PDF
  const addSignatureToPDF = async () => {
    if (selectedPostionIndex === null || !userSignature?.value) return;
    try {
      const updatedPositions = [...signaturePositions];
      const activeSignature = signaturePositions[selectedPostionIndex];
      updatedPositions[selectedPostionIndex] = {
        ...activeSignature,
        signed: true,
        signatureUrl: userSignature.value,
      };
      setSignaturePositions(updatedPositions);
      const pdfBytes = await generatePdfWithFields(updatedPositions);
      const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrlRender(url);
      setSnackbar({
        open: true,
        message: 'Signature added successfully!',
        severity: 'success',
      });

      // Navigate to next field after a short delay
      setTimeout(() => {
        navigateToNextField(activeSignature.id);
      }, 1000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error adding signature to PDF',
        severity: 'error',
      });
    }
  };

  useEffect(() => {
    if (userSignature?.value && selectedPostionIndex !== null) {
      addSignatureToPDF();
    }
  }, [userSignature]);

  const openAddSignaturePopUp = (index: number) => {
    setSelectedPostionIndex(index);
    setOpenSignature(true);
  };

  const handleFinishAndSend = async () => {
    if (!originalPdfBytesRef.current) return;
    try {
      const pdfBytesWithFields =
        await generatePdfWithFields(signaturePositions);
      const pdfDoc = await PDFDocument.load(pdfBytesWithFields);
      const metadata = {
        signatureFields: signaturePositions.map((pos) => ({
          id: pos.id,
          page: pos.page,
          x: pos.x,
          y: pos.y,
          signed: pos.signed,
          signatureUrl: pos.signatureUrl,
          signeeName: pos.signeeName,
          signeeEmail: pos.signeeEmail,
          signeeType: pos.signeeType,
          signeeId: pos.signeeId,
          source: pos.source,
        })),
        signeeUuidMap,
        version: '1.0',
        timestamp: new Date().toISOString(),
      };
      pdfDoc.setTitle('Document with Signature Fields');
      pdfDoc.setAuthor('Secure Docs');
      pdfDoc.setSubject('Document with embedded signature fields');
      pdfDoc.setKeywords(['signature', 'fields', 'secure']);
      const metadataString = JSON.stringify(metadata);
      pdfDoc.setCreationDate(new Date());
      pdfDoc.setModificationDate(new Date());
      const encodedMetadata = btoa(metadataString);
      pdfDoc.setSubject(
        `Document with embedded signature fields - Metadata: ${encodedMetadata}`
      );
      await pdfDoc.save();
      setSnackbar({
        open: true,
        message:
          'Document sent successfully! PDF created with embedded signature field data.',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error sending document',
        severity: 'error',
      });
    }
  };

  const handlePdfSave = async () => {
    if (!originalPdfBytesRef.current) return;
    try {
      const pdfBytesWithFields =
        await generatePdfWithFields(signaturePositions);
      const pdfDoc = await PDFDocument.load(pdfBytesWithFields);
      const metadata = {
        signatureFields: signaturePositions.map((pos) => ({
          id: pos.id,
          page: pos.page,
          x: pos.x,
          y: pos.y,
          signed: pos.signed,
          signatureUrl: pos.signatureUrl,
          signeeName: pos.signeeName,
          signeeEmail: pos.signeeEmail,
          signeeType: pos.signeeType,
          signeeId: pos.signeeId,
          source: pos.source,
        })),
        signeeUuidMap,
        version: '1.0',
        timestamp: new Date().toISOString(),
      };
      pdfDoc.setTitle('Document with Signature Fields');
      pdfDoc.setAuthor('Secure Docs');
      pdfDoc.setSubject('Document with embedded signature fields');
      pdfDoc.setKeywords(['signature', 'fields', 'secure']);
      const metadataString = JSON.stringify(metadata);
      pdfDoc.setCreationDate(new Date());
      pdfDoc.setModificationDate(new Date());
      const encodedMetadata = btoa(metadataString);
      pdfDoc.setSubject(
        `Document with embedded signature fields - Metadata: ${encodedMetadata}`
      );
      const finalPdfBytes = await pdfDoc.save();
      const blob = new Blob([finalPdfBytes as any], {
        type: 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'signed-document.pdf';
      link.click();
      URL.revokeObjectURL(url);
      setSnackbar({
        open: true,
        message: 'PDF downloaded successfully!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error downloading PDF',
        severity: 'error',
      });
    }
  };

  const handleEditSignature = () => {
    setOpenSignature(true);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        {/* Add CSS animation for pulse effect */}
        <style>
          {`
            @keyframes pulse {
              0% {
                transform: scale(1);
                box-shadow: 0 0 20px rgba(255, 152, 0, 0.8);
              }
              50% {
                transform: scale(1.05);
                box-shadow: 0 0 30px rgba(255, 152, 0, 1);
              }
              100% {
                transform: scale(1);
                box-shadow: 0 0 20px rgba(255, 152, 0, 0.8);
              }
            }
          `}
        </style>
        {/* Main PDF viewer area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              background: '#f8f9fa',
              mt: 2,
              mb: 2,
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 80px)',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                p: 2,
                width: '100%',
              }}
            >
              {isPdfLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <Typography>Loading PDF document...</Typography>
                </Box>
              )}
              {pdfLoadError && !isPdfLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    textAlign: 'center',
                    width: '100%',
                    minHeight: '300px',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 2, fontWeight: 500, textAlign: 'center' }}
                  >
                    Document Temporarily Unavailable
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ mb: 1, maxWidth: '400px', textAlign: 'center' }}
                  >
                    We're having trouble loading your document right now. This
                    might be due to a temporary issue with the file or our
                    servers.
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: '0.9rem', textAlign: 'center' }}
                  >
                    Please try refreshing the page in a few minutes or contact
                    support if the problem persists.
                  </Typography>
                </Box>
              )}
              {pdfFile && !isPdfLoading && !pdfLoadError && (
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  <Document
                    file={pdfUrlRender || pdfFile}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <Box
                        sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
                      >
                        <Typography>Loading PDF...</Typography>
                      </Box>
                    }
                    error={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 4,
                          textAlign: 'center',
                          width: '100%',
                          minHeight: '300px',
                        }}
                      >
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ mb: 2, fontWeight: 500, textAlign: 'center' }}
                        >
                          Document Temporarily Unavailable
                        </Typography>
                        <Typography
                          color="text.secondary"
                          sx={{ mb: 1, maxWidth: '400px', textAlign: 'center' }}
                        >
                          We're having trouble loading your document right now.
                          This might be due to a temporary issue with the file
                          or our servers.
                        </Typography>
                        <Typography
                          color="text.secondary"
                          sx={{ fontSize: '0.9rem', textAlign: 'center' }}
                        >
                          Please try refreshing the page in a few minutes or
                          contact support if the problem persists.
                        </Typography>
                      </Box>
                    }
                  >
                    {Array.from(new Array(numPages), (el, index) => {
                      const pageNumber = index + 1;
                      return (
                        <Box
                          ref={(el) => {
                            pageRefs.current[pageNumber] =
                              el as HTMLDivElement | null;
                          }}
                          sx={{ mb: 2, position: 'relative', width: '100%' }}
                          key={`paper_${index + 1}`}
                        >
                          <Page
                            key={`page_${index + 1}`}
                            pageNumber={index + 1}
                            width={Math.max(600, window.innerWidth - 520)}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                            onRenderSuccess={({ width, height }) => {
                              setPageViewports((prev) => ({
                                ...prev,
                                [index + 1]: { width, height },
                              }));
                            }}
                            onRenderError={onRenderError}
                          />
                          <DropZone
                            pageNumber={pageNumber}
                            onDropSignature={handleDropSignature}
                          />
                          {getUniqueSignaturePositions(signaturePositions).map(
                            (pos, i) => {
                              if (pos.page === index + 1 && !pos.signed) {
                                const isDisabled =
                                  activeSignerId === null ||
                                  pos.signeeId !== activeSignerId;
                                return (
                                  <DraggableSignButton
                                    enableSign={true}
                                    onClickMethod={() =>
                                      openAddSignaturePopUp(i)
                                    }
                                    signatureItem={pos}
                                    key={`signature-${pageNumber}-${i}`}
                                    sx={{
                                      position: 'absolute',
                                      top: pos.y,
                                      left: pos.x,
                                      zIndex: 10,
                                    }}
                                    signeeName={pos.signeeName}
                                    signeeType={pos.signeeType}
                                    disabled={isDisabled}
                                    isHighlighted={
                                      nextFieldToHighlight?.id === pos.id
                                    }
                                  />
                                );
                              }
                              return null;
                            }
                          )}
                        </Box>
                      );
                    })}
                  </Document>
                </Box>
              )}
            </Box>
          </Box>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
        {/* Sidebar on the right */}
        <PublicSigneesSidebar
          onFinishAndSend={handleFinishAndSend}
          onSignClick={() => setOpenSignature(true)}
          userSignature={userSignature}
          onEditSignature={handleEditSignature}
          onDownload={handlePdfSave}
          onNavigateToNextField={() => navigateToNextField()}
          areAllFieldsCompleted={areAllFieldsCompleted()}
          fieldCompletionStatus={getFieldCompletionStatus()}
          signees={signees}
          pdfFile={pdfFile}
          fields={[]}
          emailId={emailId}
          contractDetails={contractDetails}
          generatePdfWithFields={async () => {
            if (!originalPdfBytesRef.current)
              throw new Error('Original PDF bytes are not available.');
            const pdfBytesWithFields =
              await generatePdfWithFields(signaturePositions);
            const pdfDoc = await PDFDocument.load(pdfBytesWithFields);
            const metadata = {
              signatureFields: signaturePositions.map((pos) => ({
                id: pos.id,
                page: pos.page,
                x: pos.x,
                y: pos.y,
                signed: pos.signed,
                signatureUrl: pos.signatureUrl,
                signeeName: pos.signeeName,
                signeeEmail: pos.signeeEmail,
                signeeType: pos.signeeType,
                signeeId: pos.signeeId,
                fieldType: pos.fieldType,
                source: pos.source,
              })),
              signeeUuidMap,
              version: '1.0',
              timestamp: new Date().toISOString(),
            };
            pdfDoc.setTitle('Document with Signature Fields');
            pdfDoc.setAuthor('Secure Docs');
            pdfDoc.setSubject('Document with embedded signature fields');
            pdfDoc.setKeywords(['signature', 'fields', 'secure']);
            const metadataString = JSON.stringify(metadata);
            pdfDoc.setCreationDate(new Date());
            pdfDoc.setModificationDate(new Date());
            const encodedMetadata = btoa(metadataString);
            pdfDoc.setSubject(
              `Document with embedded signature fields - Metadata: ${encodedMetadata}`
            );
            return await pdfDoc.save();
          }}
          uuid={signerId}
          folderId={fileLocation}
          signaturePositions={signaturePositions}
          generatePayloadWithSigneeUuids={generatePayloadWithSigneeUuids}
          contractId={contractId}
          pdfFilePath={fileLocation}
          fullFilePath={fullFilePath}
        />
        <SignatureDialog
          open={openSignature}
          onClose={() => setOpenSignature(false)}
          onCreate={(data) => setUserSignature(data)}
          initialSignature={userSignature}
        />
      </Box>
    </DndProvider>
  );
};

export default PublicPDFSignatureViewer;
